"use client";

// src/lib/utils.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/components/Button.tsx
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { forwardRef } from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary: "bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800",
        secondary: "bg-secondary-600 text-white hover:bg-secondary-700 active:bg-secondary-800",
        outline: "border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 active:bg-neutral-100 dark:border-white/20 dark:bg-transparent dark:text-white/70 dark:hover:bg-white/5 dark:hover:text-white dark:active:bg-white/10",
        ghost: "text-neutral-700 hover:bg-neutral-100 active:bg-neutral-200 dark:text-white/70 dark:hover:bg-white/5 dark:active:bg-white/10",
        danger: "bg-error text-white hover:bg-error-dark active:bg-red-900",
        link: "text-primary-600 underline-offset-4 hover:underline"
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-4 text-sm",
        lg: "h-12 px-6 text-base",
        icon: "h-10 w-10"
      }
    },
    defaultVariants: {
      variant: "primary",
      size: "md"
    }
  }
);
var Button = forwardRef(
  ({ className, variant, size, asChild = false, loading, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx(
      Comp,
      {
        className: cn(buttonVariants({ variant, size, className })),
        ref,
        disabled: disabled || loading,
        ...props,
        children: loading ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsxs(
            "svg",
            {
              className: "h-4 w-4 animate-spin",
              xmlns: "http://www.w3.org/2000/svg",
              fill: "none",
              viewBox: "0 0 24 24",
              children: [
                /* @__PURE__ */ jsx(
                  "circle",
                  {
                    className: "opacity-25",
                    cx: "12",
                    cy: "12",
                    r: "10",
                    stroke: "currentColor",
                    strokeWidth: "4"
                  }
                ),
                /* @__PURE__ */ jsx(
                  "path",
                  {
                    className: "opacity-75",
                    fill: "currentColor",
                    d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsx("span", { children: "Caricamento..." })
        ] }) : children
      }
    );
  }
);
Button.displayName = "Button";

// src/components/Input.tsx
import { forwardRef as forwardRef2 } from "react";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var Input = forwardRef2(
  ({ className, type, label, error, helperText, leftIcon, rightIcon, id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, "-");
    return /* @__PURE__ */ jsxs2("div", { className: "w-full", children: [
      label && /* @__PURE__ */ jsx2(
        "label",
        {
          htmlFor: inputId,
          className: "mb-1.5 block text-sm font-medium text-neutral-700 dark:text-white/70",
          children: label
        }
      ),
      /* @__PURE__ */ jsxs2("div", { className: "relative", children: [
        leftIcon && /* @__PURE__ */ jsx2("div", { className: "pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-neutral-400", children: leftIcon }),
        /* @__PURE__ */ jsx2(
          "input",
          {
            type,
            id: inputId,
            className: cn(
              "flex h-10 w-full rounded-lg border bg-white px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400",
              "dark:bg-white/5 dark:text-white dark:placeholder:text-white/30",
              "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-0",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "transition-colors duration-200",
              error ? "border-error focus:border-error focus:ring-error" : "border-neutral-300 focus:border-primary-500 dark:border-white/15 dark:focus:border-brand-teal/50",
              leftIcon && "pl-10",
              rightIcon && "pr-10",
              className
            ),
            ref,
            "aria-invalid": error ? "true" : "false",
            "aria-describedby": error ? `${inputId}-error` : helperText ? `${inputId}-helper` : void 0,
            ...props
          }
        ),
        rightIcon && /* @__PURE__ */ jsx2("div", { className: "absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-400", children: rightIcon })
      ] }),
      error && /* @__PURE__ */ jsx2("p", { id: `${inputId}-error`, className: "mt-1.5 text-sm text-error", role: "alert", children: error }),
      helperText && !error && /* @__PURE__ */ jsx2("p", { id: `${inputId}-helper`, className: "mt-1.5 text-sm text-neutral-500 dark:text-white/50", children: helperText })
    ] });
  }
);
Input.displayName = "Input";

// src/components/Textarea.tsx
import { forwardRef as forwardRef3 } from "react";
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var Textarea = forwardRef3(
  ({ className, label, error, helperText, id, ...props }, ref) => {
    const textareaId = id || label?.toLowerCase().replace(/\s+/g, "-");
    return /* @__PURE__ */ jsxs3("div", { className: "w-full", children: [
      label && /* @__PURE__ */ jsx3(
        "label",
        {
          htmlFor: textareaId,
          className: "mb-1.5 block text-sm font-medium text-neutral-700 dark:text-white/70",
          children: label
        }
      ),
      /* @__PURE__ */ jsx3(
        "textarea",
        {
          id: textareaId,
          className: cn(
            "flex w-full rounded-lg border bg-white px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400",
            "dark:bg-white/5 dark:text-white dark:placeholder:text-white/30",
            "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-0",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "transition-colors duration-200",
            "resize-vertical min-h-[80px]",
            error ? "border-error focus:border-error focus:ring-error" : "border-neutral-300 focus:border-primary-500 dark:border-white/15 dark:focus:border-brand-teal/50",
            className
          ),
          ref,
          "aria-invalid": error ? "true" : "false",
          "aria-describedby": error ? `${textareaId}-error` : helperText ? `${textareaId}-helper` : void 0,
          ...props
        }
      ),
      error && /* @__PURE__ */ jsx3("p", { id: `${textareaId}-error`, className: "mt-1.5 text-sm text-error", role: "alert", children: error }),
      helperText && !error && /* @__PURE__ */ jsx3("p", { id: `${textareaId}-helper`, className: "mt-1.5 text-sm text-neutral-500 dark:text-white/40", children: helperText })
    ] });
  }
);
Textarea.displayName = "Textarea";

// src/components/Card.tsx
import { forwardRef as forwardRef4 } from "react";
import { jsx as jsx4 } from "react/jsx-runtime";
var Card = forwardRef4(({ className, ...props }, ref) => /* @__PURE__ */ jsx4(
  "div",
  {
    ref,
    className: cn(
      "rounded-xl border border-neutral-200 bg-white shadow-card dark:border-white/10 dark:bg-white/[0.04] dark:shadow-none",
      className
    ),
    ...props
  }
));
Card.displayName = "Card";
var CardHeader = forwardRef4(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx4("div", { ref, className: cn("flex flex-col space-y-1.5 p-6", className), ...props })
);
CardHeader.displayName = "CardHeader";
var CardTitle = forwardRef4(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx4(
    "h3",
    {
      ref,
      className: cn("text-lg font-semibold leading-none tracking-tight text-neutral-900 dark:text-white", className),
      ...props
    }
  )
);
CardTitle.displayName = "CardTitle";
var CardDescription = forwardRef4(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx4("p", { ref, className: cn("text-sm text-neutral-600 dark:text-white/60", className), ...props })
);
CardDescription.displayName = "CardDescription";
var CardContent = forwardRef4(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx4("div", { ref, className: cn("p-6 pt-0", className), ...props })
);
CardContent.displayName = "CardContent";
var CardFooter = forwardRef4(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx4("div", { ref, className: cn("flex items-center p-6 pt-0", className), ...props })
);
CardFooter.displayName = "CardFooter";

// src/components/Modal.tsx
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { forwardRef as forwardRef5 } from "react";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var Modal = DialogPrimitive.Root;
var ModalTrigger = DialogPrimitive.Trigger;
var ModalPortal = DialogPrimitive.Portal;
var ModalClose = DialogPrimitive.Close;
var ModalOverlay = forwardRef5(({ className, ...props }, ref) => /* @__PURE__ */ jsx5(
  DialogPrimitive.Overlay,
  {
    ref,
    className: cn(
      "fixed inset-0 z-50 bg-black/50 backdrop-blur-sm",
      "data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
      className
    ),
    ...props
  }
));
ModalOverlay.displayName = DialogPrimitive.Overlay.displayName;
var ModalContent = forwardRef5(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs4(ModalPortal, { children: [
  /* @__PURE__ */ jsx5(ModalOverlay, {}),
  /* @__PURE__ */ jsxs4(
    DialogPrimitive.Content,
    {
      ref,
      className: cn(
        "fixed left-[50%] top-[50%] z-50 w-full max-w-lg translate-x-[-50%] translate-y-[-50%]",
        "rounded-xl border border-neutral-200 bg-white p-6 shadow-lg",
        "data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
        "focus:outline-none",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsxs4(DialogPrimitive.Close, { className: "absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:pointer-events-none", children: [
          /* @__PURE__ */ jsxs4(
            "svg",
            {
              xmlns: "http://www.w3.org/2000/svg",
              width: "24",
              height: "24",
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              strokeWidth: "2",
              strokeLinecap: "round",
              strokeLinejoin: "round",
              className: "h-4 w-4",
              children: [
                /* @__PURE__ */ jsx5("path", { d: "M18 6 6 18" }),
                /* @__PURE__ */ jsx5("path", { d: "m6 6 12 12" })
              ]
            }
          ),
          /* @__PURE__ */ jsx5("span", { className: "sr-only", children: "Close" })
        ] })
      ]
    }
  )
] }));
ModalContent.displayName = DialogPrimitive.Content.displayName;
var ModalHeader = ({ className, ...props }) => /* @__PURE__ */ jsx5("div", { className: cn("flex flex-col space-y-1.5 text-center sm:text-left", className), ...props });
ModalHeader.displayName = "ModalHeader";
var ModalFooter = ({ className, ...props }) => /* @__PURE__ */ jsx5(
  "div",
  {
    className: cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className),
    ...props
  }
);
ModalFooter.displayName = "ModalFooter";
var ModalTitle = forwardRef5(({ className, ...props }, ref) => /* @__PURE__ */ jsx5(
  DialogPrimitive.Title,
  {
    ref,
    className: cn("text-lg font-semibold leading-none tracking-tight text-neutral-900", className),
    ...props
  }
));
ModalTitle.displayName = DialogPrimitive.Title.displayName;
var ModalDescription = forwardRef5(({ className, ...props }, ref) => /* @__PURE__ */ jsx5(
  DialogPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-neutral-500", className),
    ...props
  }
));
ModalDescription.displayName = DialogPrimitive.Description.displayName;

// src/components/Badge.tsx
import { cva as cva2 } from "class-variance-authority";
import { jsx as jsx6 } from "react/jsx-runtime";
var badgeVariants = cva2(
  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors",
  {
    variants: {
      variant: {
        default: "bg-neutral-100 text-neutral-800 dark:bg-white/10 dark:text-white/80",
        primary: "bg-primary-100 text-primary-800 dark:bg-primary-500/20 dark:text-primary-300",
        secondary: "bg-secondary-100 text-secondary-800 dark:bg-secondary-500/20 dark:text-secondary-300",
        success: "bg-success-light text-success-dark dark:bg-emerald-500/15 dark:text-emerald-400",
        warning: "bg-warning-light text-warning-dark dark:bg-amber-500/15 dark:text-amber-400",
        error: "bg-error-light text-error-dark dark:bg-red-500/15 dark:text-red-400",
        info: "bg-info-light text-info-dark dark:bg-blue-500/15 dark:text-blue-400",
        outline: "border border-neutral-300 text-neutral-700 dark:border-white/20 dark:text-white/70"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Badge({ className, variant, ...props }) {
  return /* @__PURE__ */ jsx6("span", { className: cn(badgeVariants({ variant }), className), ...props });
}

// src/components/Avatar.tsx
import { forwardRef as forwardRef6, useState } from "react";
import { jsx as jsx7 } from "react/jsx-runtime";
var sizeClasses = {
  xs: "h-6 w-6 text-xs",
  sm: "h-8 w-8 text-sm",
  md: "h-10 w-10 text-base",
  lg: "h-12 w-12 text-lg",
  xl: "h-16 w-16 text-xl"
};
var Avatar = forwardRef6(
  ({ className, src, alt, fallback, size = "md", ...props }, ref) => {
    const [hasError, setHasError] = useState(false);
    const initials = fallback ? fallback.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) : "?";
    if (!src || hasError) {
      return /* @__PURE__ */ jsx7(
        "div",
        {
          className: cn(
            "flex items-center justify-center rounded-full bg-neutral-100",
            sizeClasses[size],
            className
          ),
          role: "img",
          "aria-label": alt || fallback || "Avatar",
          children: /* @__PURE__ */ jsx7(
            "img",
            {
              src: "/images/logo-clean.png",
              alt: "",
              className: "h-[60%] w-[60%] object-contain opacity-60"
            }
          )
        }
      );
    }
    return /* @__PURE__ */ jsx7(
      "img",
      {
        ref,
        src,
        alt: alt || fallback || "Avatar",
        className: cn("rounded-full object-cover", sizeClasses[size], className),
        onError: () => setHasError(true),
        ...props
      }
    );
  }
);
Avatar.displayName = "Avatar";

// src/components/Spinner.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
var sizeClasses2 = {
  sm: "h-4 w-4 border-2",
  md: "h-8 w-8 border-2",
  lg: "h-12 w-12 border-3"
};
function Spinner({ className, size = "md", ...props }) {
  return /* @__PURE__ */ jsx8(
    "div",
    {
      className: cn(
        "animate-spin rounded-full border-primary-200 border-t-primary-600",
        sizeClasses2[size],
        className
      ),
      role: "status",
      "aria-label": "Loading",
      ...props,
      children: /* @__PURE__ */ jsx8("span", { className: "sr-only", children: "Loading..." })
    }
  );
}

// src/components/Alert.tsx
import { cva as cva3 } from "class-variance-authority";
import { forwardRef as forwardRef7 } from "react";
import { jsx as jsx9, jsxs as jsxs5 } from "react/jsx-runtime";
var alertVariants = cva3(
  "relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-current",
  {
    variants: {
      variant: {
        default: "bg-neutral-50 border-neutral-200 text-neutral-900 dark:bg-white/5 dark:border-white/10 dark:text-white/80",
        success: "bg-success-light border-success text-success-dark dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-400",
        warning: "bg-warning-light border-warning text-warning-dark dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-400",
        error: "bg-error-light border-error text-error-dark dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-400",
        info: "bg-info-light border-info text-info-dark dark:bg-blue-500/10 dark:border-blue-500/30 dark:text-blue-400"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var icons = {
  default: /* @__PURE__ */ jsxs5(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "20",
      height: "20",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      children: [
        /* @__PURE__ */ jsx9("circle", { cx: "12", cy: "12", r: "10" }),
        /* @__PURE__ */ jsx9("path", { d: "M12 16v-4" }),
        /* @__PURE__ */ jsx9("path", { d: "M12 8h.01" })
      ]
    }
  ),
  success: /* @__PURE__ */ jsxs5(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "20",
      height: "20",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      children: [
        /* @__PURE__ */ jsx9("path", { d: "M22 11.08V12a10 10 0 1 1-5.93-9.14" }),
        /* @__PURE__ */ jsx9("polyline", { points: "22 4 12 14.01 9 11.01" })
      ]
    }
  ),
  warning: /* @__PURE__ */ jsxs5(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "20",
      height: "20",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      children: [
        /* @__PURE__ */ jsx9("path", { d: "m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" }),
        /* @__PURE__ */ jsx9("path", { d: "M12 9v4" }),
        /* @__PURE__ */ jsx9("path", { d: "M12 17h.01" })
      ]
    }
  ),
  error: /* @__PURE__ */ jsxs5(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "20",
      height: "20",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      children: [
        /* @__PURE__ */ jsx9("circle", { cx: "12", cy: "12", r: "10" }),
        /* @__PURE__ */ jsx9("path", { d: "m15 9-6 6" }),
        /* @__PURE__ */ jsx9("path", { d: "m9 9 6 6" })
      ]
    }
  ),
  info: /* @__PURE__ */ jsxs5(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "20",
      height: "20",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      children: [
        /* @__PURE__ */ jsx9("circle", { cx: "12", cy: "12", r: "10" }),
        /* @__PURE__ */ jsx9("path", { d: "M12 16v-4" }),
        /* @__PURE__ */ jsx9("path", { d: "M12 8h.01" })
      ]
    }
  )
};
var Alert = forwardRef7(
  ({ className, variant = "default", icon, children, ...props }, ref) => /* @__PURE__ */ jsxs5(
    "div",
    {
      ref,
      role: "alert",
      className: cn(alertVariants({ variant }), className),
      ...props,
      children: [
        icon ?? icons[variant || "default"],
        children
      ]
    }
  )
);
Alert.displayName = "Alert";
var AlertTitle = forwardRef7(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx9(
    "h5",
    {
      ref,
      className: cn("mb-1 font-medium leading-none tracking-tight", className),
      ...props
    }
  )
);
AlertTitle.displayName = "AlertTitle";
var AlertDescription = forwardRef7(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx9("div", { ref, className: cn("text-sm [&_p]:leading-relaxed", className), ...props })
);
AlertDescription.displayName = "AlertDescription";
export {
  Alert,
  AlertDescription,
  AlertTitle,
  Avatar,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Modal,
  ModalClose,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalPortal,
  ModalTitle,
  ModalTrigger,
  Spinner,
  Textarea,
  badgeVariants,
  buttonVariants,
  cn
};
//# sourceMappingURL=index.mjs.map